<template>
  <el-dialog title="网络诊断" :visible.sync="dialogVisible" width="600px" append-to-body :close-on-click-modal="false"
    custom-class="network-diagnosis-dialog-shell" @closed="onDialogClosed">
    <el-scrollbar class="nd-body-scroll">
      <div class="nd-dialog">
        <section class="nd-section">
          <div class="nd-section-head">
            <span class="nd-section-indicator" aria-hidden="true" />
            <span class="nd-section-title">本机信息</span>
          </div>
          <div class="nd-local-grid">
            <div class="nd-kv">
              <span class="nd-kv-label">操作系统</span>
              <span class="nd-kv-value">{{ localInfo.os }}</span>
            </div>
            <div class="nd-kv">
              <span class="nd-kv-label">Cookie 状态</span>
              <span class="nd-kv-value nd-kv-value--inline">
                <span class="nd-dot" :class="localInfo.cookieOk ? 'nd-dot--on' : 'nd-dot--off'" />
                {{ localInfo.cookieOk ? "开启" : "关闭" }}
              </span>
            </div>
            <div class="nd-kv">
              <span class="nd-kv-label">浏览器内核</span>
              <span class="nd-kv-value">{{ localInfo.browser }}</span>
            </div>
            <div class="nd-kv">
              <span class="nd-kv-label">本地存储状态</span>
              <span class="nd-kv-value nd-kv-value--inline">
                <span class="nd-dot" :class="localInfo.storageOk ? 'nd-dot--on' : 'nd-dot--off'" />
                {{ localInfo.storageOk ? "开启" : "关闭" }}
              </span>
            </div>
          </div>
        </section>

        <section class="nd-section nd-section--spacing">
          <div class="nd-section-head">
            <span class="nd-section-indicator" aria-hidden="true" />
            <span class="nd-section-title">网络连接信息</span>
          </div>
          <div class="nd-cards">
            <div class="nd-card">
              <div class="nd-card-label">网络类型</div>
              <div class="nd-card-value">{{ connDisplay.type }}</div>
            </div>
            <div class="nd-card">
              <div class="nd-card-label">网络延迟</div>
              <div v-if="connDisplay.latency !== '—'" class="nd-card-value nd-card-value--latency">
                <span class="nd-latency-num">{{ connDisplay.latency }}</span>
                <span class="nd-latency-unit">ms</span>
              </div>
              <div v-else class="nd-card-value">—</div>
            </div>
            <div class="nd-card">
              <div class="nd-card-label">下行速度</div>
              <div class="nd-card-value">{{ connDisplay.downlink }}</div>
            </div>
          </div>

          <div class="nd-status-box">
            <div class="nd-status-icon-wrap">
              <i class="el-icon-connection nd-status-icon" />
            </div>
            <div class="nd-status-title">{{ statusTitle }}</div>
            <div class="nd-status-desc">{{ statusDesc }}</div>
          </div>
        </section>
      </div>
    </el-scrollbar>

    <span slot="footer" class="nd-footer">
      <el-button type="primary" class="nd-btn-start" :loading="diagnosing" @click="startDiagnosis">
        开始诊断
      </el-button>
    </span>
  </el-dialog>
</template>

<script>
// 解析用户代理字符串，获取操作系统和浏览器内核信息
function parseOsFromUa() {
  const ua = navigator.userAgent || "";
  const mac = ua.match(/Mac OS X ([\d_]+)/i);
  if (mac) {
    return `macOS ${mac[1].replace(/_/g, ".")}`;
  }
  if (/Windows NT 10/.test(ua)) return "Windows 10/11";
  if (/Windows NT 6\.3/.test(ua)) return "Windows 8.1";
  if (/Windows NT 6\.2/.test(ua)) return "Windows 8";
  if (/Windows NT 6\.1/.test(ua)) return "Windows 7";
  if (/Win64|WOW64|Win32/.test(ua)) return "Windows";
  if (/Android (\d+)/.test(ua)) {
    const v = ua.match(/Android (\d+)/);
    return `Android ${v ? v[1] : ""}`;
  }
  if (/iPhone OS ([\d_]+)/.test(ua)) {
    const v = ua.match(/iPhone OS ([\d_]+)/);
    return `iOS ${v ? v[1].replace(/_/g, ".") : ""}`;
  }
  return navigator.platform || "—";
}
// 解析用户代理字符串，获取浏览器内核信息
function parseBrowserKernel() {
  const ua = navigator.userAgent || "";
  const edge = ua.match(/Edg\/([\d.]+)/);
  if (edge) return `Edge/${edge[1]}`;
  const chrome = ua.match(/Chrome\/([\d.]+)/);
  if (chrome && !ua.includes("Edg")) return `Chrome/${chrome[1]}`;
  const firefox = ua.match(/Firefox\/([\d.]+)/);
  if (firefox) return `Firefox/${firefox[1]}`;
  const safari = ua.match(/Version\/([\d.]+).*Safari/);
  if (safari && !chrome) return `Safari/${safari[1]}`;
  return ua.slice(0, 48) || "—";
}


// 检查本地存储是否可用
function checkLocalStorageAvailable() {
  try {
    const k = "__nd_ls_test__";
    localStorage.setItem(k, "1");
    localStorage.removeItem(k);
    return true;
  } catch (e) {
    return false;
  }
}

// 根据网络类型，返回网络类型
function mapEffectiveType(conn) {
  const ua = navigator.userAgent || "";
  const mobile = /Mobile|Android|iPhone|iPad/i.test(ua);
  if (!mobile) {
    return "WiFi";
  }
  if (!conn || !conn.effectiveType) {
    return "蜂窝网络";
  }
  const t = conn.effectiveType;
  const map = { "4g": "4G", "3g": "3G", "2g": "2G", "slow-2g": "慢速" };
  return map[t] || String(t).toUpperCase();
}

export default {
  name: "NetworkDiagnosisDialog",
  data() {
    return {
      dialogVisible: false,
      diagnosing: false,
      /** 未诊断前占位 */
      connDisplay: {
        type: "—",
        latency: "—",
        downlink: "—"
      },
      /** 诊断阶段文案 */
      phase: "idle"
    };
  },
  computed: {
    // 获取本机信息
    localInfo() {
      return {
        os: parseOsFromUa(),
        browser: parseBrowserKernel(),
        cookieOk: !!navigator.cookieEnabled,
        storageOk: checkLocalStorageAvailable()
      };
    },
    // 获取诊断阶段文案
    statusTitle() {
      if (this.phase === "running") return "诊断中…";
      if (this.phase === "done") return "诊断完成";
      return "准备就绪";
    },
    // 获取诊断阶段描述
    statusDesc() {
      if (this.phase === "running") return "正在进行连通性与接口延迟测试，请稍候";
      if (this.phase === "done") return "您可以再次点击「开始诊断」重新测试";
      return "点击「开始诊断」按钮进行全面的网络连通性与速度测试";
    }
  },
  methods: {
    open() {
      this.phase = "idle";
      this.connDisplay = { type: "—", latency: "—", downlink: "—" };
      this.dialogVisible = true;
    },
    close() {
      this.dialogVisible = false;
    },
    onDialogClosed() {
      this.$emit("closed");
    },
    // 开始诊断
    async startDiagnosis() {
      if (this.diagnosing) return;
      this.diagnosing = true;
      this.phase = "running";

      const conn = navigator.connection || navigator.mozConnection || navigator.webkitConnection;

      try {
        const t0 = performance.now();
        await fetch(window.location.href, {
          method: "GET",
          cache: "no-store",
          credentials: "same-origin"
        }).catch(() => { });

        const probeMs = Math.max(1, Math.round(performance.now() - t0));

        const typeLabel = mapEffectiveType(conn);
        let latencyShow = "—";
        if (conn && conn.rtt != null && !Number.isNaN(conn.rtt)) {
          latencyShow = String(Math.round(conn.rtt));
        } else {
          latencyShow = String(probeMs);
        }

        let downStr = "—";
        if (conn && typeof conn.downlink === "number" && !Number.isNaN(conn.downlink)) {
          const mbps = conn.downlink;
          downStr = `${mbps >= 10 ? mbps.toFixed(1) : mbps.toFixed(2)} Mbps`;
        }

        this.connDisplay = {
          type: typeLabel,
          latency: latencyShow,
          downlink: downStr
        };
        this.phase = "done";
        this.$emit("diagnosis-done", {
          ...this.connDisplay,
          rawConnection: conn || null,
          probeMs
        });
      } catch {
        this.phase = "done";
        this.$message.warning("诊断过程出现异常，请检查网络后重试");
        this.connDisplay = {
          type: conn ? mapEffectiveType(conn) : "—",
          latency: "—",
          downlink: "—"
        };
      } finally {
        this.diagnosing = false;
      }
    }
  }
};
</script>

<style scoped lang="scss">
.nd-body-scroll {
  // max-height: min(420px, 70vh);
  overflow: hidden;
}

.nd-body-scroll ::v-deep .el-scrollbar__wrap {
  overflow-x: hidden;
}

.nd-dialog {
  padding: 0 2px 8px 0;
  box-sizing: border-box;
}

.nd-section {
  &--spacing {
    margin-top: 28px;
  }
}

.nd-section-head {
  display: flex;
  align-items: center;
  gap: 8px;
  margin-bottom: 14px;
}

.nd-section-indicator {
  width: 4px;
  height: 16px;
  border-radius: 2px;
  background: #003da6;
  flex-shrink: 0;
}

.nd-section-title {
  font-size: 13px;
  font-weight: 600;
  color: #303133;
}

.nd-local-grid {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 0 20px;
}

.nd-kv {
  display: flex;
  flex-direction: column;
  gap: 6px;
  padding: 10px 0 12px;
  border-bottom: 1px solid #ebeef5;
}

.nd-kv-label {
  font-size: 12px;
  color: #909399;
}

.nd-kv-value {
  font-size: 14px;
  color: #303133;
  word-break: break-all;

  &--inline {
    display: inline-flex;
    align-items: center;
    gap: 6px;
  }
}

.nd-dot {
  width: 8px;
  height: 8px;
  border-radius: 50%;
  flex-shrink: 0;

  &--on {
    background: #67c23a;
  }

  &--off {
    background: #f56c6c;
  }
}

.nd-cards {
  display: flex;
  gap: 12px;
  align-items: stretch;
}

.nd-card {
  flex: 1;
  min-width: 0;
  padding: 14px 10px;
  text-align: center;
  background: #f5f8ff;
  border: 1px solid #e4e7ed;
  border-radius: 10px;
  box-sizing: border-box;
}

.nd-card-label {
  font-size: 12px;
  color: #909399;
  margin-bottom: 8px;
}

.nd-card-value {
  font-size: 16px;
  font-weight: 600;
  color: #303133;
}

.nd-card-value--latency {
  display: flex;
  align-items: baseline;
  justify-content: center;
  gap: 2px;
}

.nd-latency-num {
  font-size: 18px;
  font-weight: 700;
  color: #67c23a;
}

.nd-latency-unit {
  font-size: 14px;
  font-weight: 600;
  color: #303133;
}

.nd-status-box {
  margin-top: 20px;
  padding: 28px 20px;
  text-align: center;
  background: #f5f8ff;
  border: 1px solid #e4e7ed;
  border-radius: 12px;
  box-sizing: border-box;
}

.nd-status-icon-wrap {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 56px;
  height: 56px;
  margin: 0 auto 14px;
  background: #e8f1ff;
  border-radius: 14px;
}

.nd-status-icon {
  font-size: 28px;
  color: #0052d9;
}

.nd-status-title {
  font-size: 17px;
  font-weight: 600;
  color: #303133;
  margin-bottom: 8px;
}

.nd-status-desc {
  font-size: 12px;
  line-height: 1.55;
  color: #909399;
  max-width: 380px;
  margin: 0 auto;
}

.nd-footer {
  display: flex;
  justify-content: flex-end;
}

.nd-btn-start {
  border-radius: 8px;
  padding-left: 22px;
  padding-right: 22px;
  background-color: #0052d9 !important;
  border-color: #0052d9 !important;
}
</style>

<style lang="scss">
.network-diagnosis-dialog-shell.el-dialog {
  border-radius: 14px;
  // overflow: hidden;
}

.network-diagnosis-dialog-shell .el-dialog__header {
  padding: 18px 22px 12px;
  text-align: left;
}

.network-diagnosis-dialog-shell .el-dialog__title {
  font-size: 16px;
  font-weight: 600;
  color: #303133;
}

.network-diagnosis-dialog-shell .el-dialog__body {
  padding: 8px 22px 12px;
}

.network-diagnosis-dialog-shell .el-dialog__footer {
  padding: 10px 22px 20px;
  text-align: right;
}
</style>
